import React, { useLayoutEffect, useState } from "react";
import { toast } from "react-toastify";

import Modal from "../Modal";
import Button from "../Button";
import Input from "../Input";
import InputSelect from "../InputSelect";
import InstallmentsEditor from "./InstallmentsEditor";

import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { labelForInstallments } from "./helpers";

export const USAGE_OPTIONS = [
    { label: 'Vendas e compras', value: 'both' },
    { label: 'Só vendas', value: 'sale' },
    { label: 'Só compras', value: 'purchase' },
];

const YES_NO_OPTIONS = [
    { label: 'Sim', value: true },
    { label: 'Não', value: false },
];

const ACTIVE_OPTIONS = [
    { label: 'Ativa', value: true },
    { label: 'Inativa', value: false },
];

/**
 * Cadastro de uma condição de pagamento — o mesmo modal da tela de
 * configurações e do "+ Nova condição" dos selects (BDM-1324). `saved`
 * recebe a condição como o backend gravou.
 */
export const ModalPaymentTerm = ({ show, close, term = null, saved, level }) => {
    const [saving, setSaving] = useState(false);
    const [methods, setMethods] = useState(null);

    const [name, setName] = useState('');
    const [nameTouched, setNameTouched] = useState(false);
    const [usage, setUsage] = useState('both');
    const [installments, setInstallments] = useState([{ days: 0, percent: 100 }]);
    const [paymentMethodId, setPaymentMethodId] = useState(null);
    const [isDefault, setIsDefault] = useState(false);
    const [active, setActive] = useState(true);

    // Layout effect: o Modal não tem transição, e com useEffect ele pintava um
    // quadro com os dados da condição aberta antes.
    useLayoutEffect(() => {
        if (!show) return;

        setName(term?.name ?? '');
        setNameTouched(!!term);
        setUsage(term?.usage ?? 'both');
        setInstallments(term?.installments ?? [{ days: 0, percent: 100 }]);
        setPaymentMethodId(term?.payment_method_id ?? null);
        setIsDefault(term?.is_default ?? false);
        setActive(term?.active ?? true);

        // Só na primeira abertura: o modal fica montado em cada select de
        // condição, e a maioria das telas nunca chega a abri-lo.
        if (methods === null) {
            api.get(`/payment-method?active=1`).then(res => setMethods(res.data.resources)).catch(renderError);
        }
    }, [show, term]);

    // Enquanto a pessoa não digita um nome, ele acompanha as parcelas.
    const changeInstallments = rows => {
        setInstallments(rows);

        const complete = rows.filter(row => row.days !== '' && row.percent !== '');
        if (!nameTouched && complete.length) setName(labelForInstallments(complete));
    };

    const changeName = value => {
        setName(value);
        setNameTouched(true);
    };

    const save = () => {
        setSaving(true);

        const data = {
            name,
            usage,
            installments,
            payment_method_id: paymentMethodId,
            is_default: isDefault,
            active,
        };

        const request = term
            ? api.put(`/payment-term/${term.id}`, data)
            : api.post(`/payment-term`, data);

        request.then(res => {
            toast.success('Condição salva');
            saved(res.data.resource);
        }).catch(renderError).then(() => setSaving(false));
    };

    return (
        <Modal
            title={term ? 'Editar condição' : 'Nova condição'}
            show={show}
            close={close}
            level={level}
            footer={<Button type={`primary`} size={`small`} text={`Salvar`} action={save} loading={saving} />}
        >
            <div className="section">
                <div className="row">
                    <div className="col-12">
                        <InstallmentsEditor
                            installments={installments}
                            change={changeInstallments}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <Input label={`Nome`} value={name} change={changeName} hint={`É o texto que sai na proposta e no pedido.`} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-6">
                        <InputSelect label={`Usar em`} options={USAGE_OPTIONS} value={usage} change={setUsage} menuInBody />
                    </div>
                    <div className="col-6">
                        <InputSelect
                            label={`Forma de pagamento sugerida`}
                            options={[{ label: 'Nenhuma', value: null }, ...(methods ?? []).map(method => ({ label: method.name, value: method.id }))]}
                            value={paymentMethodId}
                            change={setPaymentMethodId}
                            loading={methods === null}
                            menuInBody
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-6">
                        <InputSelect
                            label={`Condição padrão`}
                            options={YES_NO_OPTIONS}
                            value={isDefault}
                            change={setIsDefault}
                            menuInBody
                            hint={`Vem preenchida quando o cliente ou fornecedor não tem condição própria.`}
                        />
                    </div>
                    <div className="col-6">
                        <InputSelect label={`Situação`} options={ACTIVE_OPTIONS} value={active} change={setActive} menuInBody />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ModalPaymentTerm;
